import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import { assetPrimaryLabel, type Asset, type Station } from '@hcc/types';
import { Button } from '../ui/Button';
import { useAssetsList } from '../../hooks/useAssets';

/**
 * Picker for choosing one currently-available asset at a given station.
 * Used inside the kit-issue drawer when staff want to issue an asset
 * that wasn't pre-assigned. Returns the picked asset via onPick — the
 * caller is responsible for adding it to the items[] state.
 */
export function AssetPickerModal({
  station,
  excludeIds = [],
  onClose,
  onPick,
}: {
  station: Station;
  /** asset IDs to hide (already added to the items[] in the caller). */
  excludeIds?: readonly string[];
  onClose: () => void;
  onPick: (asset: Asset) => void;
}) {
  const [q, setQ] = useState('');
  const list = useAssetsList({
    station,
    status: 'available',
    assignee: 'none',
    search: q.trim() || undefined,
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const visible = (list.data ?? []).filter((a) => !excludeIds.includes(a.id));

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-fg/40 p-4 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 -z-10"
      />
      <div className="aurora-fade-in flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
        <div className="flex items-start justify-between gap-3 border-b border-divider/10 px-4 py-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-accent">
              {station} pool
            </div>
            <h2 className="text-lg font-semibold tracking-tight text-fg">Pick an asset</h2>
            <p className="text-xs text-fg-muted">
              Available, unassigned items in {station}.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<X className="h-4 w-4" />}
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </Button>
        </div>

        <div className="border-b border-divider/10 px-4 py-3">
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim"
            />
            <input
              autoFocus
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tag, description, reg, serial..."
              className="block h-9 w-full rounded-token-sm border border-line-strong/20 bg-surface pl-8 pr-3 text-sm text-fg shadow-token-sm focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
            />
          </div>
        </div>

        <div className="min-h-[12rem] flex-1 overflow-y-auto">
          {list.isLoading ? (
            <div className="px-4 py-3 text-sm text-fg-muted">Loading...</div>
          ) : list.error ? (
            <div className="px-4 py-3 text-sm text-danger-soft-fg">
              {(list.error as Error).message}
            </div>
          ) : visible.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-fg-muted">
              {q.length === 0
                ? 'No available assets in this station.'
                : 'No matches.'}
            </div>
          ) : (
            <ul className="divide-y divide-divider/10">
              {visible.slice(0, 50).map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => onPick(a)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-surface-2"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-mono text-xs font-medium text-fg">
                        {assetPrimaryLabel(a)}
                      </div>
                      <div className="truncate text-xs text-fg-muted">
                        {a.description}
                        {a.category ? ` · ${a.category}` : ''}
                      </div>
                    </div>
                    <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-medium text-accent">
                      Add
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
